import type { Dispatch, SetStateAction } from 'react'
import {
  COVERAGE_REPORT_STATUS_LABEL,
  type CoverageLookups,
  type CoverageReportFilters,
  type CoverageReportRow,
  type CoverageReportStatus,
} from './coverageReport'
import type { ReportFilter } from './ReportControls'
import { supervisorLabel } from './supervisorLabel'

export type CoverageFilterId = 'team' | 'supervisor' | 'shift' | 'assignment' | 'status'
export type CoverageFilterState = Omit<CoverageReportFilters, 'from' | 'to'>

const distinct = (values: (string | null)[]) => Array.from(new Set(values.flatMap((value) => (value ? [value] : []))))

/**
 * The filters the reports built on the coverage view offer, with the choices
 * drawn from the rows in range. A report names the ones it supports.
 */
export function coverageFilters(
  rows: CoverageReportRow[],
  lookups: CoverageLookups,
  filters: CoverageFilterState,
  setFilters: Dispatch<SetStateAction<CoverageFilterState>>,
  ids: CoverageFilterId[],
): ReportFilter[] {
  const all: Record<CoverageFilterId, ReportFilter> = {
    team: {
      id: 'team',
      label: 'Team',
      allLabel: 'All teams',
      value: filters.teamId ?? '',
      options: distinct(rows.map((row) => row.teamId)).map((id) => ({ value: id, label: lookups.teams.get(id) ?? 'Unknown team' })),
      onChange: (value) => setFilters((current) => ({ ...current, teamId: value || undefined })),
    },
    supervisor: {
      id: 'supervisor',
      label: 'Supervisor',
      allLabel: 'All supervisors',
      value: filters.supervisorId ?? '',
      options: distinct(rows.map((row) => row.supervisorId)).map((id) => ({ value: id, label: supervisorLabel(id, lookups) })),
      onChange: (value) => setFilters((current) => ({ ...current, supervisorId: value || undefined })),
    },
    shift: {
      id: 'shift',
      label: 'Shift',
      allLabel: 'All shifts',
      value: filters.shiftId ?? '',
      options: distinct(rows.map((row) => row.shiftId)).map((id) => ({ value: id, label: lookups.shifts.get(id) ?? 'Unknown shift' })),
      onChange: (value) => setFilters((current) => ({ ...current, shiftId: value || undefined })),
    },
    assignment: {
      id: 'assignment',
      label: 'Assignment',
      allLabel: 'All assignments',
      value: filters.assignmentId ?? '',
      options: distinct(rows.map((row) => row.assignmentId)).map((id) => ({
        value: id,
        label: lookups.assignments.get(id) ?? 'Unknown assignment',
      })),
      onChange: (value) => setFilters((current) => ({ ...current, assignmentId: value || undefined })),
    },
    status: {
      id: 'status',
      label: 'Status',
      allLabel: 'All statuses',
      value: filters.status ?? '',
      options: (Object.keys(COVERAGE_REPORT_STATUS_LABEL) as CoverageReportStatus[]).map((status) => ({
        value: status,
        label: COVERAGE_REPORT_STATUS_LABEL[status],
      })),
      onChange: (value) => setFilters((current) => ({ ...current, status: (value || undefined) as CoverageReportStatus | undefined })),
    },
  }
  return ids.map((id) => all[id])
}
